import React, { ChangeEvent } from 'react';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
import { FormFieldProps, ValidationMessages } from '..';
import FieldInputErrorWrapper from '../FieldInputErrorWrapper';
import FieldLabel from '../FieldLabel';

export interface CheckboxOption {
	value: string;
	text: string;
}

export type CheckboxListWithCallbackProps = FormFieldProps & {
	items: CheckboxOption[];
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export type CheckboxListFieldWithCallbackProps = CheckboxListWithCallbackProps &
	FieldProps<CheckboxOption, FieldRenderProps<CheckboxOption, HTMLElement>>;

/**
 * Component that renders a Dropdown field component
 */
export default function CheckboxListFieldWithCallback({
	name,
	isRequired = true,
	items,
	label = '',
	afterSubmit,
	allowNull,
	beforeSubmit,
	formatOnBlur,
	isEqual,
	subscription,
	validateFields,
	onChange,
}: CheckboxListFieldWithCallbackProps) {
	return (
		<Field
			name={name}
			afterSubmit={afterSubmit}
			allowNull={allowNull}
			beforeSubmit={beforeSubmit}
			formatOnBlur={formatOnBlur}
			isEqual={isEqual}
			subscription={subscription}
			validateFields={validateFields}
			onChange={onChange}
		>
			{({ onChange, meta }) => (
				<div className={meta.dirty ? 'roc-form-group roc-form-group--dirty' : 'roc-form-group'}>
					<FieldLabel label={label} name={name} isRequired={isRequired} htmlFor={name} />
					<FieldInputErrorWrapper name={name} hideIcon>
						{items.map((checkboxItem, index) => (
							<div className="roc-checkbox-list__item" key={index}>
								<CheckBox
									name={`${name}.${index}`}
									label={checkboxItem.text}
									value={checkboxItem.value}
									onChange={onChange}
								/>
							</div>
						))}
					</FieldInputErrorWrapper>
					<ValidationMessages id={name + '-invalid'} meta={meta} />
				</div>
			)}
		</Field>
	);
}

interface CheckBoxProps {
	/**
	 * The input field `name` attribute for each CheckBox. Should be unique.
	 */
	name: string;

	/**
	 * The label displayed next to the CheckBox.
	 */
	label: string;

	/**
	 * The value for the specific CheckBox. This should generally be unique as well.
	 */
	value: string;

	/**
	 * The event handler for any changes on the CheckBox.
	 */
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

function CheckBox({ name, label, value, onChange }: CheckBoxProps) {
	return (
		<label htmlFor={name}>
			<input name={name} type="checkbox" value={value} onChange={onChange} />

			<span>{label}</span>
		</label>
	);
}
